import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useForm, type Resolver } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useGetProjectsQuery, useSelectedProjectId } from 'features/projects';
import { useAppSelector } from 'shared/hooks/redux';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';

import {
  useAddIdentityProjectMutation,
  useCreateUserFullMutation,
  useGetAssignableRolesQuery,
  useGetUserFullQuery,
  usePreviewIdentityChangeMutation,
  useUpdateIdentityMutation,
  useUpdateUserFullMutation,
} from '../services/users.api';
import {
  IDENTITY_PROJECT_CONFLICT_MESSAGE,
  identityChangeNeedsConfirm,
  identityFieldsFromPreview,
  type IdentityChangeFields,
  type IdentityChangePreview,
} from '../types/identityChangePreview';
import {
  FullUserBody,
  IdentityProjectSummary,
  RoleWithPermissions,
  UpdateIdentityBody,
  UserFullResponse,
} from '../types/user';
import {
  createUserFormSchema,
  editUserAccountAdminFormSchema,
  editUserAccountFormSchema,
  editUserProjectPermissionsFormSchema,
  CreateUserFormValues,
} from '../utils/userSchema';
import IdentityExistsConfirmDialog from './IdentityExistsConfirmDialog';
import IdentityProjectChangesConfirmDialog, {
  type IdentityProjectChangeItem,
} from './IdentityProjectChangesConfirmDialog';
import IdentitySharedChangeConfirmDialog from './IdentitySharedChangeConfirmDialog';
import RolesPermissionsSection from './RolesPermissionsSection';
import UserActiveConfirmDialog, { type UserActiveConfirmAction } from './UserActiveConfirmDialog';
import UserInfoSection from './UserInfoSection';
import {
  UsersDialog as Dialog,
  UsersDialogContent as DialogContent,
  UsersDialogDescription as DialogDescription,
  UsersDialogFooter as DialogFooter,
  UsersDialogHeader as DialogHeader,
  UsersDialogTitle as DialogTitle,
} from './ui/UsersDialog';

type Mode = 'create' | 'edit';
export type EditUserDialogVariant = 'userConfig' | 'projectPermissions';
type Variant = 'full' | EditUserDialogVariant;

interface Props {
  mode: Mode;
  variant?: EditUserDialogVariant;
  userId?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FormValues = CreateUserFormValues;

const emptyDefaults = (defaultProjectId: number | null): FormValues => ({
  name: '',
  email: '',
  phoneNumber: '',
  externalId: '',
  password: '',
  projectId: defaultProjectId,
  projectIds: [],
  subProjectIds: [],
  isAssignable: false,
  isActive: true,
  roleIds: [],
});

const buildBody = (
  values: FormValues,
  mode: Mode,
  variant: Variant,
  preservedRoleIds: number[]
): Partial<FullUserBody> => {
  if (variant === 'projectPermissions') {
    return {
      isActive: values.isActive,
      externalId: values.externalId ? values.externalId : null,
      subProjectIds: values.subProjectIds,
      isAssignable: values.isAssignable,
      roleIds: [...new Set([...values.roleIds, ...preservedRoleIds])],
    };
  }
  const body: Partial<FullUserBody> = {
    email: values.email,
    name: values.name,
    phoneNumber: values.phoneNumber ? values.phoneNumber : null,
    externalId: values.externalId ? values.externalId : null,
    projectId: values.projectId,
    subProjectIds: values.subProjectIds,
    isAssignable: values.isAssignable,
    roleIds: [...new Set([...values.roleIds, ...preservedRoleIds])],
  };
  if (mode === 'edit') {
    body.isActive = values.isActive;
  }
  if (values.password && values.password.length > 0) {
    body.password = values.password;
  } else if (mode === 'create') {
    body.password = values.password;
  }
  return body;
};

const formValuesFromUserFull = (userFull: UserFullResponse): FormValues => ({
  name: userFull.user.name ?? '',
  email: userFull.user.email,
  phoneNumber: userFull.user.phoneNumber ?? '',
  externalId: userFull.user.externalId ?? '',
  password: '',
  projectId: userFull.user.projectId,
  projectIds: userFull.identity ? userFull.identity.projects.filter((p) => p.isActive).map((p) => p.projectId) : [],
  subProjectIds: userFull.user.subProjects.map((sp) => sp.id),
  isAssignable: userFull.user.isAssignable,
  isActive: userFull.user.isActive,
  roleIds: userFull.roleIds,
});

interface ProjectDiff {
  toAdd: number[];
  toRemoveEntries: IdentityProjectSummary[];
}

// Compares the form's projectIds against the Identity's active projects.
const computeProjectDiff = (identity: UserFullResponse['identity'], values: FormValues): ProjectDiff => {
  if (!identity) return { toAdd: [], toRemoveEntries: [] };
  const selectedIds = values.projectIds ?? [];
  const activeProjectIds = new Set(identity.projects.filter((p) => p.isActive).map((p) => p.projectId));
  const selected = new Set(selectedIds);
  return {
    toAdd: selectedIds.filter((id) => !activeProjectIds.has(id)),
    toRemoveEntries: identity.projects.filter((p) => p.isActive && !selected.has(p.projectId)),
  };
};

interface IdentityExistsErrorData {
  code: 'IDENTITY_EXISTS';
  identityId: number;
  name: string | null;
}

// POST /admin/users/full replies 409 { code: 'IDENTITY_EXISTS', identityId, name }
// when the email already belongs to an Identity (see routes/admin/users.ts).
const isIdentityExistsError = (err: unknown): err is { status: number; data: IdentityExistsErrorData } => {
  if (!err || typeof err !== 'object') return false;
  const { status, data } = err as { status?: unknown; data?: unknown };
  if (status !== 409 || !data || typeof data !== 'object') return false;
  return (data as Record<string, unknown>).code === 'IDENTITY_EXISTS';
};

type UserFormSchema =
  | typeof createUserFormSchema
  | typeof editUserAccountFormSchema
  | typeof editUserAccountAdminFormSchema
  | typeof editUserProjectPermissionsFormSchema;

const schemaFor = (variant: Variant, isAdminTarget: boolean): UserFormSchema => {
  if (variant === 'userConfig') {
    return isAdminTarget ? editUserAccountAdminFormSchema : editUserAccountFormSchema;
  }
  if (variant === 'projectPermissions') return editUserProjectPermissionsFormSchema;
  return createUserFormSchema;
};

const SAVE_ERROR_FALLBACK = 'No se pudo guardar. Intenta nuevamente.';

const UserConfigDialog = ({ mode, variant, userId, open, onOpenChange }: Props) => {
  const selectedProjectId = useSelectedProjectId();
  const currentUserId = useAppSelector((state) => state.user.id);
  const originalIsActiveRef = useRef<boolean | null>(null);
  const preservedRoleIdsRef = useRef<number[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<UserActiveConfirmAction | null>(null);
  const [pendingProjectDiff, setPendingProjectDiff] = useState<ProjectDiff | null>(null);
  const [pendingIdentityCollision, setPendingIdentityCollision] = useState<{
    identityId: number;
    name: string | null;
  } | null>(null);
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);
  const [identityPreview, setIdentityPreview] = useState<IdentityChangePreview | null>(null);
  const [identityChanging, setIdentityChanging] = useState<IdentityChangeFields>({
    email: false,
    name: false,
    password: false,
  });

  const effectiveVariant: Variant = mode === 'create' ? 'full' : variant ?? 'userConfig';
  const showsRoles = effectiveVariant !== 'userConfig';

  const { data: userFull, isFetching: isLoadingUser } = useGetUserFullQuery(mode === 'edit' && userId ? userId : 0, {
    skip: mode !== 'edit' || !userId,
  });
  const isAdminTarget = Boolean(userFull?.user.isAdmin);

  const form = useForm<FormValues>({
    resolver: zodResolver(schemaFor(effectiveVariant, isAdminTarget)) as unknown as Resolver<FormValues>,
    defaultValues: emptyDefaults(mode === 'create' ? selectedProjectId : null),
  });
  const { data: projects = [] } = useGetProjectsQuery();
  const projectNameById = useMemo(() => new Map(projects.map((p) => [p.id, p.name])), [projects]);
  const { data: assignableRolesResponse, isFetching: isLoadingRoles } = useGetAssignableRolesQuery(
    { withPermissions: true },
    { skip: !showsRoles }
  );
  const assignableRoles = useMemo(
    () => (assignableRolesResponse?.roles ?? []) as RoleWithPermissions[],
    [assignableRolesResponse]
  );
  const assignableRoleIdSet = useMemo(() => new Set(assignableRoles.map((role) => role.id)), [assignableRoles]);
  const [createUser, { isLoading: isCreating }] = useCreateUserFullMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserFullMutation();
  const [updateIdentity, { isLoading: isUpdatingIdentity }] = useUpdateIdentityMutation();
  const [addIdentityProject, { isLoading: isAddingProject }] = useAddIdentityProjectMutation();
  const [previewIdentityChange, { isLoading: isPreviewing }] = usePreviewIdentityChangeMutation();

  const isSubmitting = isCreating || isUpdating || isUpdatingIdentity || isAddingProject || isPreviewing;

  const canToggleActive =
    mode === 'edit' && userId !== undefined && userId !== currentUserId && !userFull?.user.isPendingActivation;

  useEffect(() => {
    if (mode !== 'edit' || !userFull) return;
    originalIsActiveRef.current = userFull.user.isActive;
    form.reset(formValuesFromUserFull(userFull));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, userFull?.user.id, form]);

  useEffect(() => {
    if (mode !== 'edit' || !userFull || isLoadingRoles) return;
    preservedRoleIdsRef.current = userFull.roleIds.filter((id) => !assignableRoleIdSet.has(id));
  }, [mode, userFull?.user.id, userFull?.roleIds, assignableRoleIdSet, isLoadingRoles]);

  const resetFormToLoadedUser = (): void => {
    if (mode !== 'edit' || !userFull) return;
    form.reset(formValuesFromUserFull(userFull));
  };

  const handleClose = (): void => {
    setSubmitError(null);
    setConfirmAction(null);
    setPendingProjectDiff(null);
    setPendingIdentityCollision(null);
    setPendingValues(null);
    setIdentityPreview(null);
    originalIsActiveRef.current = null;
    preservedRoleIdsRef.current = [];
    form.reset();
    onOpenChange(false);
  };

  const handleCloseConfirmDialog = (): void => {
    setConfirmAction(null);
    setPendingProjectDiff(null);
    setPendingValues(null);
    setSubmitError(null);
    resetFormToLoadedUser();
  };

  // Cancelling the Identity collision leaves the form as-is (there's no
  // "loaded user" to reset to — this is creation), so the email/project can
  // still be corrected.
  const handleCloseIdentityCollision = (): void => {
    setPendingIdentityCollision(null);
    setPendingValues(null);
    setSubmitError(null);
  };

  // Saves "Configuración Usuario": credentials via Identity (rename or merge
  // this User), phone on the User, then add/remove projects. Credentials run
  // first so a merge is committed before any project writes; after a merge the
  // project diff is skipped — it was computed against the source Identity.
  const submitUserConfigValues = async (values: FormValues): Promise<void> => {
    const identity = userFull?.identity ?? null;
    let emailAction: 'unchanged' | 'renamed' | 'merged' | undefined;

    if (identity && userId) {
      const identityBody: UpdateIdentityBody = {
        name: values.name,
        email: values.email,
        userId,
      };
      if (values.password && values.password.length > 0) {
        identityBody.password = values.password;
      }
      const updatedIdentity = await updateIdentity({ identityId: identity.id, body: identityBody }).unwrap();
      emailAction = updatedIdentity.emailAction;
    }

    if (userId) {
      await updateUser({
        userId,
        body: { phoneNumber: values.phoneNumber ? values.phoneNumber : null },
      }).unwrap();
    }

    if (!identity || isAdminTarget || emailAction === 'merged') {
      return;
    }

    const { toAdd, toRemoveEntries } = computeProjectDiff(identity, values);
    const projectCalls: Promise<unknown>[] = [
      ...toAdd.map((projectId) => addIdentityProject({ identityId: identity.id, projectId }).unwrap()),
      ...toRemoveEntries.map((entry) => updateUser({ userId: entry.userId, body: { isActive: false } }).unwrap()),
    ];
    if (projectCalls.length === 0) return;

    const results = await Promise.allSettled(projectCalls);
    const rejected = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (rejected) throw rejected.reason;
  };

  const submitValues = async (values: FormValues): Promise<void> => {
    setSubmitError(null);
    try {
      if (mode === 'edit' && effectiveVariant === 'userConfig') {
        await submitUserConfigValues(values);
      } else {
        const body = buildBody(values, mode, effectiveVariant, preservedRoleIdsRef.current);
        if (mode === 'create') {
          await createUser(body as FullUserBody).unwrap();
        } else if (userId) {
          await updateUser({ userId, body }).unwrap();
        }
      }
      handleClose();
    } catch (err) {
      if (mode === 'create' && isIdentityExistsError(err)) {
        setPendingValues(values);
        setPendingIdentityCollision({ identityId: err.data.identityId, name: err.data.name });
        return;
      }
      setSubmitError(extractErrorMessage(err, SAVE_ERROR_FALLBACK));
    }
  };

  // Confirming the Identity collision adds the project to the existing
  // Identity and applies the rest of what was already filled in the form
  // (POST .../projects only accepts projectId, so without this second call
  // phone/sub-projects/roles would be silently lost).
  const handleConfirmAddToProject = async (): Promise<void> => {
    if (!pendingValues || !pendingIdentityCollision) return;
    const values = pendingValues;
    if (values.projectId === null) return;
    const { identityId } = pendingIdentityCollision;
    setSubmitError(null);
    try {
      const { userId: newUserId } = await addIdentityProject({
        identityId,
        projectId: values.projectId,
      }).unwrap();
      await updateUser({
        userId: newUserId,
        body: {
          phoneNumber: values.phoneNumber ? values.phoneNumber : null,
          externalId: values.externalId ? values.externalId : null,
          subProjectIds: values.subProjectIds,
          isAssignable: values.isAssignable,
          roleIds: values.roleIds,
        },
      }).unwrap();
      handleClose();
    } catch (err) {
      setPendingIdentityCollision(null);
      setPendingValues(null);
      setSubmitError(extractErrorMessage(err, SAVE_ERROR_FALLBACK));
    }
  };

  const identityDirty = (values: FormValues): IdentityChangeFields => {
    if (mode !== 'edit' || !userFull) {
      return { email: false, name: false, password: false };
    }
    const currentEmail = (values.email ?? userFull.user.email ?? '').trim().toLowerCase();
    const loadedEmail = (userFull.user.email ?? '').trim().toLowerCase();
    return {
      email: currentEmail !== loadedEmail,
      name: (values.name ?? userFull.user.name ?? '').trim() !== (userFull.user.name ?? '').trim(),
      password: Boolean(values.password),
    };
  };

  const isActiveToggled = (values: FormValues): UserActiveConfirmAction | null => {
    if (mode !== 'edit') return null;
    const original = originalIsActiveRef.current;
    if (original === true && values.isActive === false) return 'deactivate';
    if (original === false && values.isActive === true) return 'reactivate';
    return null;
  };

  // Runs once the fields shared with Identity (email/name/password) either
  // didn't change or already got the rename/merge preview confirmed — checks
  // our own variant-specific confirms (activate/deactivate, project diff)
  // before falling through to submitValues. Permiso por Proyecto has no
  // projectIds; admin Configuración Usuario does not apply project changes.
  const continueAfterIdentityCheck = (values: FormValues): void => {
    const activeAction = isActiveToggled(values);
    if (activeAction) {
      setPendingValues(values);
      setConfirmAction(activeAction);
      return;
    }
    if (mode === 'edit' && effectiveVariant === 'userConfig' && !isAdminTarget) {
      const diff = computeProjectDiff(userFull?.identity ?? null, values);
      if (diff.toAdd.length > 0 || diff.toRemoveEntries.length > 0) {
        setPendingValues(values);
        setPendingProjectDiff(diff);
        return;
      }
    }
    void submitValues(values);
  };

  const onSubmit = (values: FormValues): void => {
    setSubmitError(null);
    if (mode === 'edit' && userId) {
      const changing = identityDirty(values);
      if (changing.email || changing.name || changing.password) {
        void (async () => {
          try {
            const preview = await previewIdentityChange({
              userId,
              email: (values.email ?? userFull?.user.email ?? '').trim(),
            }).unwrap();
            if (preview.conflictOnCurrentProject) {
              setSubmitError(IDENTITY_PROJECT_CONFLICT_MESSAGE);
              return;
            }
            const fields = identityFieldsFromPreview(preview, changing);
            if (identityChangeNeedsConfirm(preview, fields)) {
              setPendingValues(values);
              setIdentityChanging(fields);
              setIdentityPreview(preview);
              return;
            }
            continueAfterIdentityCheck(values);
          } catch (err) {
            setSubmitError(extractErrorMessage(err, 'No se pudo verificar el cambio de cuenta.'));
          }
        })();
        return;
      }
    }
    continueAfterIdentityCheck(values);
  };

  // After a merge the destination account already owns its projects. Applying
  // the form's projectIds would write against the source account and look like
  // the user was added in the wrong place.
  const handleConfirmIdentity = (): void => {
    if (!pendingValues) return;
    const values = pendingValues;
    const wasMerge = identityPreview?.action === 'merged';
    setIdentityPreview(null);
    setPendingValues(null);
    if (wasMerge) {
      void submitValues(values);
      return;
    }
    continueAfterIdentityCheck(values);
  };

  const handleCloseIdentityDialog = (): void => {
    setIdentityPreview(null);
    setPendingValues(null);
  };

  const handleConfirmSave = (): void => {
    if (!pendingValues) return;
    const values = pendingValues;
    setConfirmAction(null);
    setPendingProjectDiff(null);
    setPendingValues(null);
    void submitValues(values);
  };

  const pendingProjectChanges = useMemo(() => {
    if (!pendingProjectDiff) return null;
    const toAdd: IdentityProjectChangeItem[] = pendingProjectDiff.toAdd.map((id) => ({
      id,
      name: projectNameById.get(id) ?? `#${id}`,
    }));
    const toRemove: IdentityProjectChangeItem[] = pendingProjectDiff.toRemoveEntries.map((entry) => ({
      id: entry.projectId,
      name: entry.projectName,
    }));
    return { toAdd, toRemove };
  }, [pendingProjectDiff, projectNameById]);

  const title = useMemo(() => {
    if (mode === 'create') return 'Crear usuario';
    return effectiveVariant === 'projectPermissions' ? 'Permiso por Proyecto' : 'Configuración Usuario';
  }, [mode, effectiveVariant]);
  const description = useMemo(() => {
    if (mode === 'create') return 'Completa los datos para crear un nuevo usuario.';
    return userFull ? userFull.user.email : 'Cargando…';
  }, [mode, userFull]);

  const userLabel = userFull?.user.name?.trim() || userFull?.user.email || '';

  const contentClassName =
    effectiveVariant === 'userConfig'
      ? 'sm:max-w-lg flex max-h-[90vh] flex-col overflow-hidden'
      : 'sm:max-w-5xl flex max-h-[90vh] flex-col overflow-hidden';

  const renderFormBody = (): JSX.Element => {
    if (effectiveVariant === 'userConfig') {
      return <UserInfoSection form={form} mode={mode} variant="userConfig" isAdminUser={isAdminTarget} />;
    }
    return (
      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-2 gap-6 md:divide-x md:divide-border-subtle">
        <div className="flex min-h-0 flex-col md:pr-6">
          <UserInfoSection
            form={form}
            mode={mode}
            variant={effectiveVariant}
            canToggleActive={canToggleActive}
            isPendingActivation={Boolean(userFull?.user.isPendingActivation)}
            isSelf={mode === 'edit' && userId === currentUserId}
          />
        </div>
        <div className="flex min-h-0 flex-col md:pl-6">
          <RolesPermissionsSection form={form} roles={assignableRoles} isLoading={isLoadingRoles} />
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
        <DialogContent className={contentClassName}>
          <DialogHeader className="shrink-0">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          {mode === 'edit' && isLoadingUser && !userFull ? (
            <p className="text-sm text-text-subtle py-8 text-center">Cargando usuario…</p>
          ) : (
            // FormProvider requires spreading the RHF methods.
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Form {...form}>
              <form
                onSubmit={(e) => {
                  void form.handleSubmit(onSubmit)(e);
                }}
                className="flex min-h-0 flex-1 flex-col gap-6"
              >
                {/* Absorbs the leftover height so the lists inside shrink instead of growing the dialog. */}
                <div className="flex min-h-0 flex-1 flex-col">{renderFormBody()}</div>

                {submitError && (
                  <div className="shrink-0 rounded-md border border-error/30 bg-error-light px-3 py-2 text-sm text-error">
                    {submitError}
                  </div>
                )}

                <DialogFooter className="shrink-0">
                  <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (isPreviewing ? 'Verificando…' : 'Guardando…') : 'Guardar'}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      <IdentitySharedChangeConfirmDialog
        open={identityPreview !== null}
        preview={identityPreview}
        changing={identityChanging}
        currentProjectId={pendingValues?.projectId ?? userFull?.user.projectId}
        isLoading={isCreating || isUpdating || isUpdatingIdentity}
        onClose={handleCloseIdentityDialog}
        onConfirm={handleConfirmIdentity}
      />

      <UserActiveConfirmDialog
        open={confirmAction !== null}
        action={confirmAction}
        userLabel={userLabel}
        isLoading={isSubmitting}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmSave}
      />

      <IdentityProjectChangesConfirmDialog
        open={pendingProjectChanges !== null}
        toAdd={pendingProjectChanges?.toAdd ?? []}
        toRemove={pendingProjectChanges?.toRemove ?? []}
        isLoading={isSubmitting}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmSave}
      />

      <IdentityExistsConfirmDialog
        open={pendingIdentityCollision !== null}
        identityName={pendingIdentityCollision?.name ?? null}
        isLoading={isSubmitting}
        onClose={handleCloseIdentityCollision}
        onConfirm={() => {
          void handleConfirmAddToProject();
        }}
      />
    </>
  );
};

export default UserConfigDialog;
